// apps/web/src/components/primitives/isian/ringkasan.ts
/**
 * The error summary at the top of a form (spec 2026-09-30-sapuan-isian, 3.6), without React:
 * one item per field with a problem, in the order of the form (the order the page lists
 * them), "Label: sentence"; a sentence that already opens with its label stands alone ("Pemisah
 * wajib diisi…", never "Pemisah: Pemisah wajib diisi…", uji D9). A field without its sentence
 * still gets a line.
 */
import { format, uiStrings } from '@satu/shared'

const teks = uiStrings.isian.ringkasan

export interface KontrolGalat {
  /** The id of the element the item moves the focus to. */
  kunci: string
  label: string
  pesan: string | null
}

export interface ItemRingkasan {
  kunci: string
  teks: string
}

export function itemRingkasan(kontrol: readonly KontrolGalat[]): ItemRingkasan[] {
  const dilihat = new Set<string>()
  const out: ItemRingkasan[] = []
  for (const k of kontrol) {
    if (dilihat.has(k.kunci)) continue
    dilihat.add(k.kunci)
    const label = k.label.trim()
    const pesan = (k.pesan ?? '').trim() || teks.periksa
    const sendiri = label === '' || pesan.toLowerCase().startsWith(`${label.toLowerCase()} `)
    out.push({ kunci: k.kunci, teks: sendiri ? pesan : format(teks.item, { label, pesan }) })
  }
  return out
}

/**
 * The sentences the summary keeps while a field hides its own: a NIK's or phone's count
 * sentence waits while the person types in the field (`pesanNomorTampil`,
 * `pesanTeleponTampil`), and the field, still wrong, then names no sentence. Its line keeps
 * the sentence it last had instead of turning into "Periksa isian ini." when its link puts the
 * focus there. `ingat` holds the last sentence of each field listed; a field no longer listed
 * is forgotten.
 */
export function pesanTetap(
  kontrol: readonly KontrolGalat[],
  ingat: ReadonlyMap<string, string>,
): { kontrol: KontrolGalat[]; ingat: Map<string, string> } {
  const baru = new Map<string, string>()
  const out = kontrol.map((k) => {
    const pesan = k.pesan !== null && k.pesan.trim() !== '' ? k.pesan : ingat.get(k.kunci)
    if (pesan === undefined) return k
    baru.set(k.kunci, pesan)
    return pesan === k.pesan ? k : { ...k, pesan }
  })
  return { kontrol: out, ingat: baru }
}

export function judulRingkasan(jumlah: number): string {
  return format(teks.judul, { jumlah })
}

export function samaRingkasan(a: readonly ItemRingkasan[], b: readonly ItemRingkasan[]): boolean {
  return (
    a.length === b.length && a.every((x, i) => x.kunci === b[i]?.kunci && x.teks === b[i]?.teks)
  )
}
